import React, { useEffect, useState } from "react";
import { useAuth } from "context/AuthContext";
import { API, graphqlOperation } from "aws-amplify";
import { createPropertyFeature, updatePropertyFeature } from "graphql/mutations";
import { usePropertyData } from "context/PropertyDataContext";
import Card from "components/common/Card";
import { notify } from "utilities/notify";
import FormGroup from "components/common/FormGroup";

export default function UseRestrictions(props) {
  const { className, autorizedUser, setHasUnsavedChanges, handleFieldChange, updateFormCompletion } = props;
  const { propertyData, refresh } = usePropertyData();
  const { user } = useAuth();

  const [formData, setFormData] = useState({});
  const [executedOnce, setExecutedOnce] = useState(false);
  const [resDescPfID, setResDescPfID] = useState(null);
  const [resOtherPfID, setResOtherPfID] = useState(null);
  const [changedFields, setChangedFields] = useState({});
  
  const checkFormCompletion = () => {
    if (!executedOnce) return; // ✅ Evita ejecución antes de inicialización
  
    // ✅ Se considera completado si al menos un campo tiene datos
    const hasAnyData = Object.values(formData).some((value) => value.trim() !== "");
  
    console.log("🔍 Estado de UseRestrictions:", formData);
    console.log("✅ ¿Formulario completado?", hasAnyData);
  
    updateFormCompletion(hasAnyData); // ✅ Se marca como completado si hay cualquier dato
  };
  
  
  
  
  useEffect(() => {
    if (executedOnce && Object.keys(formData).length > 0) {
      checkFormCompletion();
    }
  }, [formData, executedOnce]); // ✅ Asegurar que se ejecuta cuando `formData` está listo y el efecto no corre antes de tiempo
  

  useEffect(() => {
    if (propertyData && propertyData.propertyFeatures && user && !executedOnce) {
      const pfIDResDesc = propertyData.propertyFeatures.find(
        (item) => item.featureID === "E_restriccion_desc"
      )?.id || null;
      setResDescPfID(pfIDResDesc);

      const pfIDResOther = propertyData.propertyFeatures.find(
        (item) => item.featureID === "E_resctriccion_other"
      )?.id || null;
      setResOtherPfID(pfIDResOther);

      setFormData({
        projectRestrictionsDesc: propertyData.projectRestrictions?.desc || "",
        projectRestrictionsOther: propertyData.projectRestrictions?.other || "",
      });

      setChangedFields({}); // Inicializa sin cambios
      setExecutedOnce(true);
    }
  }, [propertyData, user]);


  const handleChangeInputValue = (e) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Marcar el campo como cambiado
    setChangedFields((prev) => ({
      ...prev,
      [name]: value !== propertyData.projectRestrictions?.[name.replace("projectRestrictions", "").toLowerCase()],
    }));

    handleFieldChange(name, true);
    setHasUnsavedChanges(true);
    checkFormCompletion();
  };

  const handleSaveBtn = async () => {
    try {
      let updates = [];

      if (changedFields["projectRestrictionsDesc"]) {
        const featureID = "E_restriccion_desc";
        const featureValue = formData.projectRestrictionsDesc;

        if (resDescPfID) {
          updates.push(
            API.graphql(
              graphqlOperation(updatePropertyFeature, {
                input: { id: resDescPfID, value: featureValue },
              })
            )
          );
        } else {
          updates.push(
            API.graphql(
              graphqlOperation(createPropertyFeature, {
                input: {
                  propertyID: propertyData.propertyInfo.id,
                  featureID,
                  value: featureValue,
                },
              })
            ).then((response) => setResDescPfID(response.data.createPropertyFeature.id))
          );
        }
      }

      if (changedFields["projectRestrictionsOther"]) {
        const featureID = "E_resctriccion_other";
        const featureValue = formData.projectRestrictionsOther;

        if (resOtherPfID) {
          updates.push(
            API.graphql(
              graphqlOperation(updatePropertyFeature, {
                input: { id: resOtherPfID, value: featureValue },
              })
            )
          );
        } else {
          updates.push(
            API.graphql(
              graphqlOperation(createPropertyFeature, {
                input: {
                  propertyID: propertyData.propertyInfo.id,
                  featureID,
                  value: featureValue,
                },
              })
            ).then((response) => setResOtherPfID(response.data.createPropertyFeature.id))
          );
        }
      }

      await Promise.all(updates);

      // Limpiar los cambios después de guardar
      updateFormCompletion(true);
      setChangedFields({});
      handleFieldChange("projectRestrictionsDesc", false);
      handleFieldChange("projectRestrictionsOther", false);

      notify({ msg: "Información actualizada", type: "success" });
      setHasUnsavedChanges(false);
      refresh();
      checkFormCompletion();
    } catch (error) {
      console.error("Error al guardar:", error);
      notify({ msg: "Error al guardar la información", type: "error" });
    }
  };

  return (
    <Card className={className}>
      <Card.Header title="Limitaciones de uso de suelo" sep={true} />
      <Card.Body>
        <div className="row row-cols-1">
          <div className="col">
            <FormGroup
              disabled={!autorizedUser}
              inputType="textarea"
              label="Restricción de uso por encontrarse inmerso en áreas de protección declaradas como parques, zonas de reserva, otros"
              inputName="projectRestrictionsDesc"
              inputValue={formData.projectRestrictionsDesc}
              onChangeInputValue={(e) => handleChangeInputValue(e)}
              className={`border border-terrasacha-light rounded-lg p-1 ${
                changedFields["projectRestrictionsDesc"] ? "border-terrasacha-danger bg-red-100" : ""
              }`}
            />
          </div>
          <div className="col">
            <FormGroup
              disabled={!autorizedUser}
              inputType="textarea"
              label="Otros limitantes"
              inputName="projectRestrictionsOther"
              inputValue={formData.projectRestrictionsOther}
              onChangeInputValue={(e) => handleChangeInputValue(e)}
              className={`border border-terrasacha-light rounded-lg p-1 ${
                changedFields["projectRestrictionsOther"] ? "border-terrasacha-danger bg-red-100" : ""
              }`}
            />
          </div>
        </div>

        {/* Botón único para guardar todos los cambios */}
        {autorizedUser && (
          <div className="flex justify-center mt-6">
            <button
              type="button"
              className="btn-terrasacha-primary px-6 py-2.5 font-typographica"
              onClick={handleSaveBtn}
              disabled={!Object.values(changedFields).some((changed) => changed)}
              aria-label="Guardar limitaciones de uso de suelo"
            >
              Guardar
            </button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}